import { BadgeCheck, ExternalLink, Lock, Star } from "lucide-react";

import type { AppEntitlement } from "@/types/app";
import { cn } from "@/utils/cn";

type Props = {
  entitlement: AppEntitlement;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  onLaunch: (entitlement: AppEntitlement) => void;
};

export function AppListRow({
  entitlement,
  isFavorite,
  onToggleFavorite,
  onLaunch,
}: Props) {
  const { app, visible } = entitlement;
  const disabled = !visible || app.status === "coming_soon" || !app.url;
  const isUrlIcon = /^https?:\/\//.test(app.icon);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-tile border border-slate-200 bg-white px-3 py-2 shadow-tile transition hover:shadow-tileHover",
        disabled && "opacity-75",
      )}
    >
      <div
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded text-lg",
          app.accent ?? "bg-slate-100 text-slate-700",
        )}
        aria-hidden="true"
      >
        {isUrlIcon ? (
          <img src={app.icon} alt="" className="h-6 w-6 rounded" />
        ) : (
          <span>{app.icon}</span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="truncate text-sm font-semibold text-slate-900">
            {app.name}
          </div>
          {!visible ? (
            <span className="chip bg-red-50 text-red-700">
              <Lock className="h-3 w-3" /> Restricted
            </span>
          ) : app.assignedToMe ? (
            <span className="chip bg-emerald-50 text-emerald-700">
              <BadgeCheck className="h-3 w-3" /> Assigned
            </span>
          ) : null}
        </div>
        <div className="truncate text-xs text-slate-500">{app.description}</div>
      </div>

      <span className="chip shrink-0">{app.category}</span>

      {onToggleFavorite ? (
        <button
          type="button"
          onClick={() => onToggleFavorite(app.id)}
          aria-label={isFavorite ? "Remove favorite" : "Add favorite"}
          aria-pressed={isFavorite}
          className="rounded p-1 text-slate-400 hover:text-amber-500"
        >
          <Star
            className={cn(
              "h-4 w-4",
              isFavorite && "fill-amber-400 text-amber-500",
            )}
          />
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => onLaunch(entitlement)}
        disabled={disabled}
        className="btn-secondary text-xs"
      >
        Launch
        {app.launchType !== "same_tab" ? (
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        ) : null}
      </button>
    </div>
  );
}
